import { useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Circle, Lock, ArrowRight, Clock } from 'lucide-react'
import { ROUTES } from '@/constants/routes'
import type { OnboardingStatusResponse } from '@/types/operator.types'
import { cn } from '@/lib/utils'

interface Props {
  data: OnboardingStatusResponse
}

/**
 * Bacheca onboarding 7-step. Visualizza ogni step con stato e CTA contestuale
 * per quello "current". Compatto: una card unica con elenco verticale.
 */
export function OnboardingChecklist({ data }: Props) {
  const { onboarding, profile } = data
  const navigate = useNavigate()

  // Riepilogo "passi mancanti": tutti gli step non ancora done.
  const remainingSteps = onboarding.steps.filter((s) => s.status !== 'done').length
  // L'admin ha richiesto modifiche: l'operatore può modificare il profilo e
  // re-inviare la candidatura (→ APPLIED). Usiamo `status` come segnale
  // autoritativo: copre sia i profili nuovi (onboardingStep=NEEDS_REVISION) sia
  // quelli precedenti al fix (onboardingStep=APPLIED, status=NEEDS_REVISION).
  const isRevisionRequested =
    profile.onboardingStep === 'NEEDS_REVISION' || profile.status === 'NEEDS_REVISION'

  function ctaFor(stepKey: string): { label: string; href: string } | null {
    switch (stepKey) {
      case 'CONSENT_GIVEN':
        // CONSENT_GIVEN = privacy già accettata, prossima azione = compila il
        // profilo professionale (il wizard parte dai dati personali). I dati
        // fiscali si raccolgono dopo l'approvazione e il contratto (step FISCAL_DATA).
        return { label: 'Compila il profilo', href: ROUTES.OPERATOR.WIZARD }
      case 'APPLIED':
        // L'invio è automatico al termine del wizard: la CTA porta al data-entry.
        return { label: 'Completa la candidatura', href: ROUTES.OPERATOR.WIZARD }
      case 'CONTRACT_ACCEPTED':
        return { label: 'Accetta contratto', href: ROUTES.OPERATOR.ONBOARDING_CONTRACT }
      case 'FISCAL_DATA':
        return { label: 'Completa dati fiscali', href: ROUTES.OPERATOR.ONBOARDING_FISCAL_DATA }
      case 'SUBSCRIBED':
        return { label: 'Attiva abbonamento', href: ROUTES.OPERATOR.ONBOARDING_SUBSCRIPTION }
      case 'PROFILE_PAYMENTS':
      case 'PROFILE_CONTENT':
        return null  // sub-gates renderizzati separatamente sotto
      default:
        return null
    }
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Stato profilo operatore</h2>
          {remainingSteps > 0 && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              {remainingSteps === 1
                ? 'Manca 1 passo prima della pubblicazione'
                : `Mancano ${remainingSteps} passi prima della pubblicazione`}
            </p>
          )}
        </div>
        <span className="text-xs text-gray-500">
          Step {Math.min(
            onboarding.steps.findIndex((s) => s.status === 'current') + 1,
            onboarding.steps.length,
          )} di {onboarding.steps.length}
        </span>
      </div>

      {/* Banner prominente: l'admin ha richiesto modifiche al profilo */}
      {isRevisionRequested && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-600" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-orange-900">
              Il team OLLYlife ha richiesto delle modifiche
            </p>
            <p className="mt-1 rounded-md border border-orange-200 bg-white px-3 py-2 text-sm text-gray-700">
              {profile.moderationNote}
            </p>
            <button
              onClick={() => navigate(ROUTES.OPERATOR.EDIT)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:underline"
            >
              Modifica il profilo e re-invia <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      <ol className="space-y-3">
        {onboarding.steps.map((s) => {
          // In revisione la CTA d'azione è il banner arancione in alto
          // ("Modifica il profilo e re-invia"): evitiamo il doppione sulla riga.
          const cta = s.status === 'current' && !(isRevisionRequested && s.key === 'APPLIED')
            ? ctaFor(s.key)
            : null
          return (
            <li
              key={s.key}
              className={cn(
                'flex items-start gap-3 rounded-xl border px-4 py-3 transition-colors',
                s.status === 'done'    && 'border-green-100 bg-green-50',
                s.status === 'current' && 'border-holly-200 bg-holly-50',
                s.status === 'locked'  && 'border-gray-100 bg-gray-50 opacity-60',
              )}
            >
              <span className="mt-0.5">
                {s.status === 'done'    && <CheckCircle2 className="h-5 w-5 text-green-600" />}
                {s.status === 'current' && <Circle       className="h-5 w-5 text-holly-600" />}
                {s.status === 'locked'  && <Lock         className="h-5 w-5 text-gray-400" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className={cn(
                  'text-sm font-medium',
                  s.status === 'done'    && 'text-green-900',
                  s.status === 'current' && 'text-gray-900',
                  s.status === 'locked'  && 'text-gray-500',
                )}>
                  {s.label}
                </p>

                {/* Sub-gates: Abbonamento e Stripe Connect */}
                {s.key === 'PROFILE_PAYMENTS' && s.status !== 'locked' && s.status !== 'done' && (
                  <ul className="mt-2 space-y-1 text-xs">
                    <GateRow
                      ok={onboarding.gates.subscription_active}
                      label="Abbonamento attivo"
                      ctaLabel={!onboarding.gates.subscription_active ? 'Attiva' : undefined}
                      ctaHref={ROUTES.OPERATOR.ONBOARDING_SUBSCRIPTION}
                    />
                    <GateRow
                      ok={onboarding.gates.stripe_connect_verified}
                      label="Stripe Connect verificato"
                      ctaLabel={!onboarding.gates.stripe_connect_verified ? 'Configura' : undefined}
                      ctaHref={ROUTES.OPERATOR.ONBOARDING_STRIPE_CONNECT}
                    />
                  </ul>
                )}

                {/* Sub-gates: Servizi e Blog */}
                {s.key === 'PROFILE_CONTENT' && s.status !== 'locked' && s.status !== 'done' && (
                  <ul className="mt-2 space-y-1 text-xs">
                    {/* Di solito già verde (l'invio del wizard precede la candidatura):
                        lo mostriamo solo quando manca, altrimenti il blocco è invisibile. */}
                    {!onboarding.gates.wizard_submitted && (
                      <GateRow
                        ok={false}
                        label="Profilo pubblico confermato"
                        ctaLabel="Rivedi e invia"
                        ctaHref={ROUTES.OPERATOR.WIZARD}
                      />
                    )}
                    <GateRow
                      ok={onboarding.gates.first_paid_service}
                      label="Almeno 1 servizio a pagamento"
                      ctaLabel={!onboarding.gates.first_paid_service ? 'Aggiungi servizio' : undefined}
                      ctaHref={ROUTES.OPERATOR.SERVICES}
                    />
                    <GateRow
                      ok={onboarding.gates.first_article_published}
                      label="Almeno 1 articolo approvato dalla redazione"
                      pending={onboarding.articleInReview}
                      note={onboarding.articleInReview ? 'In revisione' : undefined}
                      ctaLabel={
                        !onboarding.gates.first_article_published && !onboarding.articleInReview
                          ? 'Scrivi articolo'
                          : undefined
                      }
                      ctaHref={ROUTES.OPERATOR.ARTICLE_NEW}
                    />
                  </ul>
                )}
                {s.key === 'PROFILE_CONTENT' && onboarding.articleInReview
                  && s.status !== 'locked' && s.status !== 'done' && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700">
                    <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    Hai già inviato un articolo: è in revisione presso la redazione di OLLYlife.
                    Non serve scriverne altri.
                  </p>
                )}
              </div>

              {cta && (
                <button
                  onClick={() => navigate(cta.href)}
                  className="ml-2 inline-flex shrink-0 items-center gap-1 rounded-lg bg-holly-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-holly-700"
                >
                  {cta.label} <ArrowRight className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          )
        })}
      </ol>

      {/* Stato "in attesa di approvazione admin" — solo quando non c'è una nota di revisione */}
      {onboarding.currentStep === 'APPLIED' && !isRevisionRequested && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900">
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            La tua candidatura è in revisione. Riceverai una email entro 2 giorni lavorativi.
          </span>
        </div>
      )}
    </div>
  )
}

function GateRow({
  ok, label, ctaLabel, ctaHref, pending, note,
}: {
  ok: boolean
  label: string
  ctaLabel?: string
  ctaHref?: string
  /** Stato "in attesa" (es. articolo in revisione): icona orologio ambra. */
  pending?: boolean
  /** Testo di stato non cliccabile a destra (ha precedenza sul CTA). */
  note?: string
}) {
  const navigate = useNavigate()
  return (
    <li className="flex items-center gap-2">
      {ok
        ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
        : pending
          ? <Clock  className="h-3.5 w-3.5 shrink-0 text-amber-500" />
          : <Circle className="h-3.5 w-3.5 shrink-0 text-gray-400" />}
      <span className={ok ? 'text-green-900' : pending ? 'text-amber-800' : 'text-gray-700'}>{label}</span>
      {note
        ? <span className="ml-auto font-medium text-amber-700">{note}</span>
        : ctaLabel && ctaHref && !ok && (
            <button
              onClick={() => navigate(ctaHref)}
              className="ml-auto text-holly-700 hover:underline"
            >
              {ctaLabel} →
            </button>
          )}
    </li>
  )
}
